import { showError } from "@/utils/toastUtils";
import React from "react";
import { Sk } from "../../../../components/Skeleton";
import { useGetDecisionsQuery } from "../../../api/nebula-stats/nebulaStatsApi";
import { NebulaDecisionIcon } from "../../../components/icons";
import { reformatDate } from "../utils/dateUtils";
import {
  DECISION_TYPE_LABEL,
  PLAN_TYPES,
  PORTFOLIO_TYPE,
} from "../utils/nebulaData";
import { getDecisionHeading, groupByDate } from "../utils/nebulaUtils";
import BlockNoteRenderer from "./BlockNoteRenderer";
import DecisionPlanModal from "./DecisionPlanModal";
import PlanDetailModal from "./PlanDetailModal";
import Spinner from "./Spinner";
import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { WarningTriangleOutlineIcon } from "./icons/WarningTriangleOutlineIcon";
import { DateIcon } from "./icons/DateIcon";
import { NextIcon } from "./icons/NextIcon";
import { InfoIcon } from "./icons/InfoIcon";

const PLAN_LINKED_TYPES = ["REBALANCE_EXECUTED", "FIXED_INCOME_EXECUTED"];
const PLAN_DETAIL_TYPES = ["REBALANCE_PLANNED"];

const SEVERITY_TAG = {
  INFO: {
    label: "Info",
    textColor: "#006CEC",
    bg: "rgba(0, 108, 236, 0.1)",
  },
  WARN: {
    label: "Cảnh báo",
    textColor: "#C9991D",
    bg: "rgba(254, 242, 211, 1)",
    Icon: () => <WarningTriangleOutlineIcon color="#FBBF24" />,
  },
  CRITICAL: {
    label: "Khẩn cấp",
    textColor: "#D95656",
    bg: "rgba(255, 225, 225, 1)",
    cardBg: "rgba(255, 107, 107, 0.05)",
    Icon: () => <WarningTriangleOutlineIcon color="#FF6B6B" />,
  },
};

const PLAN_TAG = {
  label: "Kế hoạch",
  textColor: "#866FC8",
  bg: "rgba(167, 139, 250, 0.1)",
  cardBg: "rgba(167, 139, 250, 0.05)",
  Icon: DateIcon,
};

const getDecisionTag = (item) =>
  (PLAN_TYPES.includes(item.type) ? PLAN_TAG : null) ??
  SEVERITY_TAG[item.severity] ??
  SEVERITY_TAG.INFO;

const mapDecisions = (data) => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => {
    const blocks = Array.isArray(item.blocks) ? item.blocks : [];
    return {
      decisionId: item.decisionId,
      tradeDate: reformatDate(item.tradeDate, "DD/MM/YYYY"),
      tradeTime: reformatDate(item.tradeDate, "HH:mm"),
      seq: item.seq,
      type: item.type,
      severity: item.severity,
      tag: getDecisionTag(item),
      heading: getDecisionHeading(blocks),
      blocks,
      planId: PLAN_LINKED_TYPES.includes(item.type) ? item.planId : null,
      explainId: PLAN_DETAIL_TYPES.includes(item.type) ? item.decisionId : null,
    };
  });
};

const PAGE_SIZE = 5;

const DecisionTab = ({ decision, className = "" }) => {
  const [decisions, setDecisions] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [hasMore, setHasMore] = React.useState(true);
  const [activePlanId, setActivePlanId] = React.useState(null);
  const [activeExplainId, setActiveExplainId] = React.useState(null);
  const scrollRef = React.useRef(null);
  const sentinelRef = React.useRef(null);

  const { data, isLoading, isFetching, isError, refetch } =
    useGetDecisionsQuery({
      type: PORTFOLIO_TYPE.PAPER,
      decision,
      page,
      size: PAGE_SIZE,
    });

  React.useEffect(() => {
    if (isError) showError("Không thể tải dữ liệu");
  }, [isError]);

  React.useEffect(() => {
    if (!data?.data) return;
    const newItems = mapDecisions(data.data);
    setDecisions((prev) => {
      if (page === 0) return newItems;
      const seen = new Set(prev.map((d) => d.decisionId));
      return [...prev, ...newItems.filter((d) => !seen.has(d.decisionId))];
    });
    const total = data.total;
    setHasMore(
      typeof total === "number"
        ? page * PAGE_SIZE + data.data.length < total
        : newItems.length === PAGE_SIZE,
    );
  }, [data, page]);

  React.useEffect(() => {
    if (!sentinelRef.current || isFetching || isLoading || !hasMore) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setPage((p) => p + 1);
      },
      { root: scrollRef.current },
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [isFetching, isLoading, hasMore, decisions]);

  const grouped = React.useMemo(() => groupByDate(decisions), [decisions]);

  if (decisions.length === 0 && (isLoading || isFetching)) {
    return (
      <div className="flex flex-col h-full overflow-y-auto py-2">
        {Array.from({ length: 7 }).map((i) => (
          <div key={i} className="px-5 pb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sk w={24} h={24} r="rounded-full" />
                <Sk w={54} h={14} />
              </div>
              <Sk w={104} h={24} r="rounded-lg" />
            </div>
            <div className="flex flex-col gap-2">
              <Sk h={16} className="w-2/5" />
              <div className="flex flex-col gap-1.5">
                <Sk h={12} className="w-full" />
                <Sk h={12} className="w-11/12" />
                <Sk h={12} className="w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (decisions.length === 0 && isError) {
    return (
      <ErrorState
        className="h-full"
        description="Không thể tải nhật ký quyết định."
        actionText="Thử lại"
        onAction={() => refetch()}
      />
    );
  }

  if (decisions.length === 0) {
    return (
      <EmptyState
        className="h-full"
        text={
          <span className="flex flex-col gap-1">
            <span className="text-[15px] font-[500] text-[#111827]">
              Chưa có quyết định nào
            </span>
            <span className="text-[14px] text-[#636369]">
              Nebula sẽ ghi lại các quyết định danh mục tại đây.
            </span>
          </span>
        }
      />
    );
  }

  return (
    <div
      ref={scrollRef}
      className="relative flex flex-col h-full overflow-y-auto"
    >
      <div className="flex flex-col flex-1">
        {grouped.map(({ date, label, entries }) => (
          <div key={date}>
            <div className="flex items-center gap-2 px-[20px] pt-[16px]">
              <div
                className="flex-1 h-px"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, #ececed 0, #ececed 4px, transparent 4px, transparent 8px)",
                }}
              />
              <span className="text-[10px] font-bold text-[#A0A0A5] leading-[16px]">
                {label}
              </span>
              <div
                className="flex-1 h-px"
                style={{
                  backgroundImage:
                    "repeating-linear-gradient(to right, #ececed 0, #ececed 4px, transparent 4px, transparent 8px)",
                }}
              />
            </div>

            {entries.map((entry) => (
              <div key={entry.decisionId}>
                <div
                  className={`px-[20px] ${entry.tag.cardBg ? "py-[8px] mt-[8px]" : "pt-[16px] pb-[0px]"} ${className}`}
                  style={
                    entry.tag.cardBg
                      ? { backgroundColor: entry.tag.cardBg }
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between mb-[8px]">
                    <div className="flex items-center gap-2">
                      <NebulaDecisionIcon size={24} />
                      <span className="text-[14px] font-[500] text-[#111827]">
                        Nebula
                      </span>
                    </div>
                    <span
                      className="flex items-center gap-[8px] text-[12px] font-[500] leading-[18px] px-[8px] py-[3px] rounded-[8px] flex-shrink-0"
                      style={{
                        color: entry.tag.textColor,
                        backgroundColor: entry.tag.bg,
                      }}
                    >
                      {entry.tag.Icon && <entry.tag.Icon />}
                      {entry.tag.label}
                    </span>
                  </div>

                  {entry.tradeDate && (
                    <div className="text-[12px] leading-[16px] text-[#636369] mb-[4px]">
                      {entry.tradeDate}
                    </div>
                  )}

                  <div className="rounded-xl flex flex-col gap-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[14px] font-[700] text-[#111827]">
                        {entry.heading ||
                          DECISION_TYPE_LABEL[entry.type] ||
                          entry.type}
                      </span>
                    </div>
                    <BlockNoteRenderer
                      blocks={entry.blocks.filter(
                        (block) =>
                          !(
                            block.type === "heading" && block.props?.level === 2
                          ),
                      )}
                    />
                    {entry.planId != null && (
                      <button
                        type="button"
                        onClick={() => setActivePlanId(entry.planId)}
                        className="flex items-center gap-[4px]"
                      >
                        <InfoIcon />
                        <span className="text-[12px] leading-[18px] text-[#636369] font-[500] self-start hover:text-[#111827]  transition-colors">
                          Thực hiện theo đúng kế hoạch.
                        </span>
                        <span className="text-[12px] leading-[18px] text-[#111827] font-[500] hover:underline flex items-end gap-[2px]">
                          Xem lại
                          <NextIcon />
                        </span>
                      </button>
                    )}
                    {entry.explainId != null && (
                      <button
                        type="button"
                        onClick={() => setActiveExplainId(entry.explainId)}
                        className="flex items-center gap-[4px] self-start"
                      >
                        <InfoIcon />
                        <span className="text-[12px] leading-[18px] text-[#111827] font-[500] hover:underline flex items-end gap-[2px]">
                          Xem chi tiết
                          <NextIcon />
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))}

        {isError && decisions.length > 0 && (
          <div className="flex flex-col items-center justify-center p-4 text-center gap-2">
            <span className="text-[12px] text-[#EF4444]">
              Không thể tải thêm quyết định.
            </span>
            <button
              onClick={() => refetch()}
              className="text-[12px] font-semibold text-[#3B82F6] hover:underline"
            >
              Thử lại
            </button>
          </div>
        )}

        {hasMore && !isFetching && !isError && (
          <div ref={sentinelRef} className="h-4 w-full" />
        )}
      </div>

      {isFetching && decisions.length > 0 && (
        <div className="sticky bottom-0 pointer-events-none flex flex-col justify-end">
          <div
            className="h-12"
            style={{
              background:
                "linear-gradient(to top, #ffffff 40%, rgba(255,255,255,0))",
              backdropFilter: "blur(2px)",
              WebkitMaskImage: "linear-gradient(to top, #000 50%, transparent)",
              maskImage: "linear-gradient(to top, #000 50%, transparent)",
            }}
          />
          <div className="flex items-center justify-center gap-2 py-3 bg-white">
            <Spinner />
            <span className="text-[12px] text-[#636369]">Đang tải thêm...</span>
          </div>
        </div>
      )}

      {activePlanId != null && (
        <DecisionPlanModal
          planId={activePlanId}
          onClose={() => setActivePlanId(null)}
        />
      )}

      {activeExplainId != null && (
        <PlanDetailModal
          decisionId={activeExplainId}
          onClose={() => setActiveExplainId(null)}
        />
      )}
    </div>
  );
};

export default DecisionTab;
